import axiosInstance from './axiosInstance'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuditUser {
  id:        string
  name:      string
  email:     string
  avatarUrl: string | null
}

export interface OrgEmailFile {
  id:          string
  orgId:       string
  orgName:     string
  lobId:       string | null
  lobName:     string | null
  fileName:    string
  url:         string
  contentType: string
  sizeBytes:   number
  uploadedBy:  AuditUser | null
  createdAt:   string
}

// ── API ───────────────────────────────────────────────────────────────────────

export const orgEmailFilesApi = {
  /**
   * List pre-uploaded files.
   * - SUPER_ADMIN: pass orgId/lobId to filter, or omit orgId to get all orgs.
   * - OrgAdmin: orgId is ignored (backend scopes to caller's org); lobId optionally narrows.
   * - Normal User: orgId/lobId are ignored; the backend always scopes to the caller's org+LOB.
   */
  getAll: (orgId?: string | null, lobId?: string | null): Promise<OrgEmailFile[]> =>
    axiosInstance
      .get<{ data: OrgEmailFile[] }>('/org-email-files', {
        params: {
          ...(orgId ? { orgId } : {}),
          ...(lobId ? { lobId } : {}),
        },
      })
      .then(r => r.data.data),

  /**
   * Upload a file for the given organisation + Line of Business.
   * orgId is required when the caller is SUPER_ADMIN; ignored otherwise.
   * lobId is required for SUPER_ADMIN / OrgAdmin; ignored for Normal Users
   * (who are locked server-side to their own LOB).
   */
  upload: (
    file:        File,
    orgId?:      string | null,
    lobId?:      string | null,
    onProgress?: (pct: number) => void,
  ): Promise<OrgEmailFile> => {
    const form = new FormData()
    form.append('file', file)
    if (orgId) form.append('orgId', orgId)
    if (lobId) form.append('lobId', lobId)

    return axiosInstance
      .post<{ data: OrgEmailFile }>('/org-email-files', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total))
        },
      })
      .then(r => r.data.data)
  },

  /** Delete a pre-uploaded file by its id. */
  delete: (id: string): Promise<void> =>
    axiosInstance.delete(`/org-email-files/${id}`).then(() => undefined),
}

// ── File-type helpers (shared with UI) ────────────────────────────────────────

export function getFileIcon(contentType: string): string {
  if (contentType.startsWith('image/'))                                return '🖼️'
  if (contentType === 'application/pdf')                               return '📄'
  if (contentType.includes('word') || contentType.includes('document'))return '📝'
  if (contentType.includes('sheet') || contentType.includes('excel'))  return '📊'
  if (contentType.includes('presentation') || contentType.includes('power')) return '📋'
  return '📎'
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024)          return `${bytes} B`
  if (bytes < 1024 * 1024)   return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export const ACCEPTED_FILE_TYPES =
  'application/pdf,image/*,' +
  'application/msword,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.ms-excel,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'application/vnd.ms-powerpoint,' +
  'application/vnd.openxmlformats-officedocument.presentationml.presentation'
